import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Chip, FormControl, InputLabel, Select,
  MenuItem, List, ListItem, ListItemText, ListItemIcon, Divider, IconButton,
  Tooltip, Switch, FormControlLabel, Stack, Container, CircularProgress,
  Autocomplete, Collapse, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Avatar, Grid
} from "@mui/material";
import {
  Warning as WarningIcon, Save as SaveIcon, History as HistoryIcon,
  Person as PersonIcon, Receipt as ReceiptIcon, Search as SearchIcon,
  Delete as DeleteIcon, Add as AddIcon, PictureAsPdf as PdfIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon,
  Edit as EditIcon, ManageAccounts as ManageAccountsIcon
} from "@mui/icons-material";
import api, { generateUserPDF } from "../services/api";
import { AVAILABLE_COURSES } from "../constants/courses";

const USER_BASE_URL = process.env.REACT_APP_USER_API_URL || "https://backend.hunarmandpunjab.org.pk";

interface User {
  _id: string; rollNumber: string; email: string; fullName: string; fatherName: string;
  cnic: string; mobile: string; dateOfBirth: string; gender: "male" | "female";
  qualification: string; courses: string[]; permanentAddress: string; city: string;
  isVerified: boolean; createdAt: string; updatedAt: string;
  scholarship?: {
    _id: string; fullName: string; cnic: string; rollNumber: string; email: string;
    mobileNumber: string; challanNumber: string; imagePath?: string;
    status: "pending" | "approved" | "rejected"; appliedAt: string; createdAt: string; updatedAt: string;
  } | null;
}

interface Challan {
  _id: string; userId: { _id: string; fullName: string; email: string; rollNumber: string; } | null;
  challanId: string; amount: number; paid: boolean; path?: string; branchCode?: number;
  txnDate?: string; txnId?: number; createdAt: string; updatedAt: string;
}

interface AuditLog {
  _id: string; entityType: "user" | "challan"; entityId: string; action: string;
  changes: Record<string, { old: any; new: any }>; adminUser: string;
  timestamp: string; reason?: string;
}

function EditRecords() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedChallan, setSelectedChallan] = useState<Challan | null>(null);
  const [editedUser, setEditedUser] = useState<User | null>(null);
  const [editedChallan, setEditedChallan] = useState<Challan | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [challans, setChallans] = useState<Challan[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modal states
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [auditLogModalOpen, setAuditLogModalOpen] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [changeReason, setChangeReason] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [adminApiKey, setAdminApiKey] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pendingAction, setPendingAction] = useState<"user" | "challan" | null>(null);

  useEffect(() => {
    setCurrentPage(1);
    if (activeTab === 0) fetchUsers();
    else fetchChallans();
  }, [activeTab]);

  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      if (activeTab === 0) fetchUsers();
      else fetchChallans();
    }, 300);
    setSearchTimeout(timeout);
    return () => { if (timeout) clearTimeout(timeout); };
  }, [searchQuery, activeTab]);

  useEffect(() => {
    if (activeTab === 0) fetchUsers();
    else fetchChallans();
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 25, page: currentPage };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const response = await api.get("/users", { params });
      setUsers(response.data.data.users);
      setTotalPages(response.data.data.pagination.pages);
      setTotalRecords(response.data.data.pagination.total);
    } catch (error) { console.error("Error fetching users:", error); } finally { setLoading(false); }
  };

  const fetchChallans = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 25, page: currentPage };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const response = await api.get("/challans", { params });
      setChallans(response.data.data.challans);
      setTotalPages(response.data.data.pagination.pages);
      setTotalRecords(response.data.data.pagination.total);
    } catch (error) { console.error("Error fetching challans:", error); } finally { setLoading(false); }
  };

  const fetchAuditLogs = async (entityType: string, entityId: string) => {
    try {
      const response = await api.get(`/audit-logs/${entityType}/${entityId}`);
      setAuditLogs(response.data.data);
    } catch (error) { console.error("Error fetching audit logs:", error); }
  };

  const handleUserSelect = (user: User) => {
    if (selectedUser?._id === user._id) {
      setSelectedUser(null);
      setEditedUser(null);
    } else {
      setSelectedUser(user);
      setEditedUser({ ...user });
    }
  };

  const handleChallanSelect = (challan: Challan) => {
    if (selectedChallan?._id === challan._id) {
      setSelectedChallan(null);
      setEditedChallan(null);
    } else {
      setSelectedChallan(challan);
      setEditedChallan({ ...challan });
    }
  };

  const handleEdit = (type: "user" | "challan") => {
    setPendingAction(type);
    setWarningModalOpen(true);
  };

  const confirmEdit = async () => {
    if (!changeReason.trim()) return alert("Please provide a reason for this change");
    try {
      setLoading(true);
      if (pendingAction === "user" && editedUser && selectedUser) {
        const { scholarship, ...userData } = editedUser;
        const updatePayload: any = { ...userData, reason: changeReason };
        if (scholarship && selectedUser.scholarship) updatePayload.scholarship = scholarship;
        await api.put(`/users/${selectedUser._id}`, updatePayload);
        await fetchUsers();
        setSelectedUser(null); setEditedUser(null);
      } else if (pendingAction === "challan" && editedChallan && selectedChallan) {
        await api.put(`/challans/${selectedChallan._id}`, { ...editedChallan, reason: changeReason });
        await fetchChallans();
        setSelectedChallan(null); setEditedChallan(null);
      }
      setWarningModalOpen(false); setChangeReason(""); setPendingAction(null);
    } catch (error) { console.error("Error updating record:", error); alert("Failed to update record"); } finally { setLoading(false); }
  };

  const handleViewAuditLog = (entityType: "user" | "challan", entityId: string) => {
    fetchAuditLogs(entityType, entityId);
    setAuditLogModalOpen(true);
  };

  const handleDeleteUser = () => setDeleteModalOpen(true);
  const handleGeneratePDF = () => setPdfModalOpen(true);

  const confirmGeneratePDF = async () => {
    if (!adminApiKey.trim()) return alert("Please provide the admin API key");
    if (!selectedUser) return alert("No user selected");
    try {
      setPdfGenerating(true);
      const response = await generateUserPDF(selectedUser._id, adminApiKey);
      const data = response.data;
      if (data.status === "success" && data.data && data.data.fileUrl) {
        const fileUrl = data.data.fileUrl;
        const uploadPath = fileUrl.substring(fileUrl.indexOf("/uploads"));
        window.open(`${USER_BASE_URL}${uploadPath}`, "_blank");
      } else throw new Error("Invalid response format from challan API");
      setPdfModalOpen(false); setAdminApiKey("");
    } catch (error: any) { alert(`Failed to generate challan: ${error.response?.data?.message || error.message}`); } finally { setPdfGenerating(false); }
  };

  const confirmDeleteUser = async () => {
    if (!deleteReason.trim()) return alert("Please provide a reason for deletion");
    if (!selectedUser) return alert("No user selected");
    try {
      setLoading(true);
      await api.delete(`/users/${selectedUser._id}`, { data: { reason: deleteReason } });
      await fetchUsers();
      setSelectedUser(null); setEditedUser(null); setDeleteModalOpen(false); setDeleteReason("");
      alert("User and all related data deleted successfully");
    } catch (error) { console.error("Error deleting user:", error); alert("Failed to delete user"); } finally { setLoading(false); }
  };

  const getInitials = (name: string) => name.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2);

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", pb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Box sx={{ bgcolor: 'rgba(5, 150, 105, 0.1)', p: 1.5, borderRadius: '12px', mr: 2 }}>
          <EditIcon sx={{ fontSize: 32, color: '#059669' }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
            Edit Records
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500, mt: 0.25 }}>
            Modify user profiles, challans, and scholarships directly from the database.
          </Typography>
        </Box>
      </Box>

      <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px', '& .MuiAlert-message': { fontWeight: 500 } }}>
        <strong>Warning:</strong> Changes made here will immediately reflect in the database. All modifications are permanently logged for audit purposes.
      </Alert>

      {/* Modern Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => { setActiveTab(newValue); setSelectedUser(null); setSelectedChallan(null); }} sx={{
          '& .MuiTabs-indicator': { bgcolor: '#059669', height: '3px', borderRadius: '3px 3px 0 0' },
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem', color: '#64748b', '&.Mui-selected': { color: '#059669' } }
        }}>
          <Tab icon={<PersonIcon />} iconPosition="start" label="Edit Student Profiles" />
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Edit Challan Records" />
        </Tabs>
      </Box>

      {/* Floating Search Bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
        <Box display="flex" alignItems="center" sx={{ position: "relative" }}>
          <SearchIcon sx={{ position: "absolute", left: 16, color: "#94a3b8" }} />
          <input
            type="text"
            placeholder={activeTab === 0 ? "Search students by Name, Email, or Roll No..." : "Search challans by ID or Student Name..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: "100%", padding: "14px 14px 14px 48px", borderRadius: "12px", border: "1px solid #cbd5e1", fontSize: "0.95rem", outline: "none", fontFamily: "inherit" }}
          />
          {loading && <CircularProgress size={20} sx={{ position: 'absolute', right: 16, color: '#059669' }} />}
        </Box>
      </Paper>

      {/* Data Table with Expandable Rows */}
      <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 20px rgba(0,0,0,0.03)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell width={40} />
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>{activeTab === 0 ? 'Student Identity' : 'Challan Info'}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>{activeTab === 0 ? 'Contact & Roll No' : 'Payment Status'}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>{activeTab === 0 ? 'Status' : 'Transaction Date'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "#475569" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (!users.length && !challans.length) ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
              ) : activeTab === 0 && users.length > 0 ? (
                users.map(user => {
                  const isExpanded = selectedUser?._id === user._id;
                  return (
                    <React.Fragment key={user._id}>
                      <TableRow hover sx={{ bgcolor: isExpanded ? "rgba(241, 245, 249, 0.5)" : "inherit", "& > *": { borderBottom: "unset" } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleUserSelect(user)} sx={{ color: isExpanded ? '#059669' : '#94a3b8' }}>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: "#3b82f6", fontWeight: 700, width: 40, height: 40 }}>{getInitials(user.fullName)}</Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>{user.fullName}</Typography>
                              <Typography variant="body2" sx={{ color: "#64748b" }}>{user.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={`Roll: ${user.rollNumber}`} sx={{ height: 22, fontSize: '0.75rem', bgcolor: '#f1f5f9', fontWeight: 600, mb: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", display: "block" }}>{user.mobile}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={user.isVerified ? "Verified" : "Unverified"} size="small" sx={{ bgcolor: user.isVerified ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", color: user.isVerified ? "#059669" : "#f59e0b", fontWeight: 700 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Button variant="outlined" size="small" onClick={() => handleUserSelect(user)} startIcon={<EditIcon />} sx={{ borderRadius: "8px", textTransform: "none", color: "#475569", borderColor: "#cbd5e1" }}>
                            {isExpanded ? 'Close Editor' : 'Edit Record'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, p: 3, bgcolor: "#f8fafc", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                              {editedUser && (
                                <UserEditForm
                                  user={editedUser}
                                  onUserChange={setEditedUser}
                                  onSave={() => handleEdit("user")}
                                  onDelete={handleDeleteUser}
                                  onGeneratePDF={handleGeneratePDF}
                                  onViewAuditLog={() => handleViewAuditLog("user", user._id)}
                                  loading={loading}
                                />
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : activeTab === 1 && challans.length > 0 ? (
                challans.map(challan => {
                  const isExpanded = selectedChallan?._id === challan._id;
                  return (
                    <React.Fragment key={challan._id}>
                      <TableRow hover sx={{ bgcolor: isExpanded ? "rgba(241, 245, 249, 0.5)" : "inherit", "& > *": { borderBottom: "unset" } }}>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleChallanSelect(challan)} sx={{ color: isExpanded ? '#059669' : '#94a3b8' }}>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: "#f59e0b", fontWeight: 700, width: 40, height: 40 }}><ReceiptIcon /></Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>Challan #{challan.challanId}</Typography>
                              <Typography variant="body2" sx={{ color: "#64748b" }}>{challan.userId?.fullName || "Unknown User"}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5 }}>Rs. {challan.amount}</Typography>
                          <Chip label={challan.paid ? "Paid" : "Unpaid"} size="small" sx={{ bgcolor: challan.paid ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)", color: challan.paid ? "#059669" : "#ef4444", fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569" }}>
                            {challan.txnDate ? new Date(challan.txnDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button variant="outlined" size="small" onClick={() => handleChallanSelect(challan)} startIcon={<EditIcon />} sx={{ borderRadius: "8px", textTransform: "none", color: "#475569", borderColor: "#cbd5e1" }}>
                            {isExpanded ? 'Close Editor' : 'Edit Record'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, p: 3, bgcolor: "#f8fafc", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                              {editedChallan && (
                                <ChallanEditForm
                                  challan={editedChallan}
                                  onChallanChange={setEditedChallan}
                                  onSave={() => handleEdit("challan")}
                                  onViewAuditLog={() => handleViewAuditLog("challan", challan._id)}
                                  loading={loading}
                                />
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5, color: '#64748b' }}>No records found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={totalRecords}
          rowsPerPage={25}
          page={currentPage - 1}
          onPageChange={(e, newPage) => setCurrentPage(newPage + 1)}
          sx={{ borderTop: "1px solid #e2e8f0" }}
        />
      </Paper>

      {/* Warning Modal (Save changes) */}
      <Dialog open={warningModalOpen} onClose={() => setWarningModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#f59e0b' }}>Confirm Modification</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
            You are about to modify core database records. This action is irreversible and will be logged to your admin account.
          </Alert>
          <TextField
            label="Reason for Change (Required)"
            placeholder="Explain why this change is being made (e.g. Fixing typo, Manual verification)..."
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            fullWidth multiline rows={3}
            InputProps={{ sx: { borderRadius: '12px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setWarningModalOpen(false)} color="inherit" disabled={loading}>Cancel</Button>
          <Button onClick={confirmEdit} variant="contained" disabled={!changeReason.trim() || loading} sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, borderRadius: '8px', boxShadow: 'none' }}>
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#ef4444' }}>Permanently Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
            <strong>DANGER:</strong> This will permanently delete the user account, their challans, and their scholarships. This cannot be undone!
          </Alert>
          {selectedUser && (
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f1f5f9', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Target Account:</Typography>
              <Typography variant="body2">{selectedUser.fullName} ({selectedUser.email})</Typography>
              <Typography variant="body2" color="text.secondary">Roll No: {selectedUser.rollNumber}</Typography>
            </Box>
          )}
          <TextField
            label="Reason for Deletion (Required)"
            placeholder="Why is this user being wiped from the database?"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            fullWidth multiline rows={3}
            InputProps={{ sx: { borderRadius: '12px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setDeleteModalOpen(false)} color="inherit" disabled={loading}>Cancel</Button>
          <Button onClick={confirmDeleteUser} variant="contained" disabled={!deleteReason.trim() || loading} sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, borderRadius: '8px', boxShadow: 'none' }} startIcon={<DeleteIcon />}>
            Delete All Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Generation Modal */}
      <Dialog open={pdfModalOpen} onClose={() => setPdfModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#0ea5e9' }}>Access Challan PDF</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
            This generates or opens the active PDF Challan document for this user via the secure API.
          </Alert>
          <TextField
            label="Admin Auth Key (Required)"
            placeholder="Enter the secure admin key..."
            value={adminApiKey}
            onChange={(e) => setAdminApiKey(e.target.value)}
            fullWidth type="password"
            InputProps={{ sx: { borderRadius: '12px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setPdfModalOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={confirmGeneratePDF} variant="contained" disabled={!adminApiKey.trim() || pdfGenerating} sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' }, borderRadius: '8px', boxShadow: 'none' }} startIcon={pdfGenerating ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}>
            {pdfGenerating ? "Processing..." : "Open Document"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log Modal */}
      <Dialog open={auditLogModalOpen} onClose={() => setAuditLogModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', borderBottom: '1px solid #e2e8f0', pb: 2 }}>Record Change History</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List sx={{ p: 0 }}>
            {auditLogs.length === 0 ? (
              <Box p={4} textAlign="center"><Typography color="text.secondary">No changes recorded for this entity.</Typography></Box>
            ) : (
              auditLogs.map((log) => (
                <div key={log._id}>
                  <ListItem sx={{ py: 2, px: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: '#334155' }}>{log.action.toUpperCase()} by @{log.adminUser}</Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: 600 }}>{new Date(log.timestamp).toLocaleString()}</Typography>
                    </Box>
                    {log.reason && (
                      <Typography variant="body2" sx={{ width: '100%', mb: 1.5, p: 1, bgcolor: '#f8fafc', borderRadius: '6px', fontStyle: 'italic', color: '#64748b' }}>
                        Reason: {log.reason}
                      </Typography>
                    )}
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {Object.entries(log.changes).map(([field, change]) => (
                        <Chip key={field} label={`${field}: ${change.old} → ${change.new}`} size="small" variant="outlined" sx={{ borderRadius: '6px', bgcolor: '#fff', color: '#475569' }} />
                      ))}
                    </Box>
                  </ListItem>
                  <Divider />
                </div>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setAuditLogModalOpen(false)} sx={{ fontWeight: 600, color: '#475569' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ================= USER EDIT FORM =================
function UserEditForm({ user, onUserChange, onSave, onDelete, onGeneratePDF, onViewAuditLog, loading }: any) {
  const [newCourse, setNewCourse] = useState("");

  const handleChange = (field: string, value: any) => onUserChange({ ...user, [field]: value });
  const handleScholarshipChange = (field: string, value: any) => {
    if (!user.scholarship) return;
    onUserChange({ ...user, scholarship: { ...user.scholarship, [field]: value } });
  };
  const handleAddCourse = () => {
    const userCourses = Array.isArray(user.courses) ? user.courses : [];
    if (newCourse.trim() && !userCourses.includes(newCourse.trim()) && AVAILABLE_COURSES.includes(newCourse.trim())) {
      handleChange("courses", [...userCourses, newCourse.trim()]);
      setNewCourse("");
    }
  };
  const handleRemoveCourse = (courseToRemove: string) => {
    const userCourses = Array.isArray(user.courses) ? user.courses : [];
    handleChange("courses", userCourses.filter((course: string) => course !== courseToRemove));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} pb={2} borderBottom="1px solid #e2e8f0">
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Edit Student Details</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="View Edit History">
            <IconButton onClick={onViewAuditLog} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}><HistoryIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid size={{ xs: 12 }}>
          <Typography sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>Personal Information</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField label="Full Name" value={user.fullName} onChange={(e) => handleChange("fullName", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField label="Father Name" value={user.fatherName} onChange={(e) => handleChange("fatherName", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><TextField label="CNIC" value={user.cnic} onChange={(e) => handleChange("cnic", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><TextField label="Date of Birth" type="date" value={user.dateOfBirth?.split("T")[0] || ""} onChange={(e) => handleChange("dateOfBirth", e.target.value)} fullWidth InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Gender</InputLabel>
            <Select value={user.gender} label="Gender" onChange={(e) => handleChange("gender", e.target.value)} sx={{ borderRadius: '10px' }}>
              <MenuItem value="male">Male</MenuItem><MenuItem value="female">Female</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Contact Info */}
        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>Contact & Location</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField label="Email Address" type="email" value={user.email} onChange={(e) => handleChange("email", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField label="Mobile Number" value={user.mobile} onChange={(e) => handleChange("mobile", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField label="City" value={user.city} onChange={(e) => handleChange("city", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField label="Address" value={user.permanentAddress} onChange={(e) => handleChange("permanentAddress", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        
        {/* Academics */}
        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>Academic Settings</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField label="Highest Qualification" value={user.qualification} onChange={(e) => handleChange("qualification", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '10px', p: 1, pl: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControlLabel control={<Switch checked={user.isVerified} onChange={(e) => handleChange("isVerified", e.target.checked)} color="success" />} label={<Typography sx={{ fontWeight: 600, color: user.isVerified ? '#059669' : '#64748b' }}>Account Verified Status</Typography>} />
          </Box>
        </Grid>

        {/* Courses Editor */}
        <Grid size={{ xs: 12 }}>
          <Box sx={{ bgcolor: '#fff', p: 2, borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155', mb: 1 }}>Enrolled Courses</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {Array.isArray(user.courses) && user.courses.length > 0 ? user.courses.map((course: string, index: number) => (
                <Chip key={index} label={course} onDelete={() => handleRemoveCourse(course)} color="primary" variant="outlined" sx={{ borderRadius: '8px', fontWeight: 600 }} />
              )) : <Typography variant="body2" color="text.secondary">No active enrollments.</Typography>}
            </Box>
            <Box display="flex" gap={1}>
              <Autocomplete
                options={AVAILABLE_COURSES.filter(c => !(Array.isArray(user.courses) ? user.courses : []).includes(c))}
                value={newCourse} onChange={(e, nv) => setNewCourse(nv || "")}
                renderInput={(params) => <TextField {...params} label="Add Course" size="small" InputProps={{ ...params.InputProps, sx: { borderRadius: '8px' } }} />}
                sx={{ flex: 1 }}
              />
              <Button variant="contained" onClick={handleAddCourse} disabled={!newCourse.trim() || !AVAILABLE_COURSES.includes(newCourse.trim())} sx={{ borderRadius: '8px', boxShadow: 'none' }}><AddIcon /></Button>
            </Box>
          </Box>
        </Grid>

        {/* Scholarship */}
        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>Scholarship Management</Typography>
          {user.scholarship ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}><TextField label="Challan ID" value={user.scholarship.challanNumber || ""} onChange={(e) => handleScholarshipChange("challanNumber", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={user.scholarship.status} label="Status" onChange={(e) => handleScholarshipChange("status", e.target.value)} sx={{ borderRadius: '10px' }}>
                    <MenuItem value="pending">Pending</MenuItem><MenuItem value="approved">Approved</MenuItem><MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}><TextField label="Image Document Path" value={user.scholarship.imagePath || ""} onChange={(e) => handleScholarshipChange("imagePath", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} /></Grid>
            </Grid>
          ) : (
            <Alert severity="info" sx={{ borderRadius: '10px' }}>No scholarship profile exists for this student. Applications must originate from the student portal.</Alert>
          )}
        </Grid>

        {/* Actions */}
        <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={onDelete} disabled={loading} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>Delete User</Button>
            <Button variant="contained" color="info" startIcon={<PdfIcon />} onClick={onGeneratePDF} disabled={loading} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>Access Challan</Button>
            <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={onSave} disabled={loading} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>Save Profile Changes</Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

// ================= CHALLAN EDIT FORM =================
function ChallanEditForm({ challan, onChallanChange, onSave, onViewAuditLog, loading }: any) {
  const handleChange = (field: string, value: any) => onChallanChange({ ...challan, [field]: value });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} pb={2} borderBottom="1px solid #e2e8f0">
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Edit Challan Record</Typography>
        <Tooltip title="View Edit History">
          <IconButton onClick={onViewAuditLog} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}><HistoryIcon /></IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField label="Associated User" value={challan.userId ? `${challan.userId.fullName} (${challan.userId.email})` : "User Deleted / Unlinked"} fullWidth disabled InputProps={{ sx: { borderRadius: '10px', bgcolor: '#f1f5f9' } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Challan ID" value={challan.challanId} onChange={(e) => handleChange("challanId", e.target.value)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Payable Amount (Rs.)" type="number" value={challan.amount} onChange={(e) => handleChange("amount", parseInt(e.target.value))} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Transaction Code" type="number" value={challan.txnId || ""} onChange={(e) => handleChange("txnId", e.target.value ? parseInt(e.target.value) : undefined)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Bank Branch Code" type="number" value={challan.branchCode || ""} onChange={(e) => handleChange("branchCode", e.target.value ? parseInt(e.target.value) : undefined)} fullWidth InputProps={{ sx: { borderRadius: '10px' } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Transaction Date" type="date" value={challan.txnDate ? challan.txnDate.split("T")[0] : ""} onChange={(e) => handleChange("txnDate", e.target.value ? new Date(e.target.value).toISOString() : undefined)} fullWidth InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '10px', p: 1, pl: 2, display: 'flex', alignItems: 'center', height: '100%' }}>
            <FormControlLabel control={<Switch checked={challan.paid} onChange={(e) => handleChange("paid", e.target.checked)} color="success" />} label={<Typography sx={{ fontWeight: 600, color: challan.paid ? '#059669' : '#ef4444' }}>Status: {challan.paid ? 'PAID' : 'UNPAID'}</Typography>} />
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={onSave} disabled={loading} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>Save Record Changes</Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EditRecords;
